import { Typography, CardMedia, Box, Divider, Chip } from "@mui/material";
import Stack from "@mui/material/Stack";

import React, { useState } from "react";

import MoreHorizIcon from "@mui/icons-material/MoreHoriz";

import { ReactComponent as CollectionsLogo } from "assets/icons/bookmark-blue-icon.svg";
import { ReactComponent as CollectionsLogoHover } from "assets/icons/bookmark-hover-icon.svg";

import { useDispatch } from "react-redux";
import {
  setBoclipsPopup,
  setBoclipsVideoId,
  setCollectionId,
  setCollectionSourceId,
  setIsCitationAvailable,
  setIsSidebarOpen,
  setIsSummaryAvailable,
  setLevel,
  setReadingLevelAvailable,
  setSelectedBookmarkOption,
  setSummaryInfoData,
} from "context/features/dataSlice";
import defaultBackground from "assets/images/image-placeholder.png";
import { logEvent } from "services/amplitude.service";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import { formatDuration } from "utils/time.util";
import { searchAPI } from "services/apis.service";

import dayjs from "lib/dayjs";
import AccessibleIconButton from "components/base/AccessibleIconButton";

export const BoclipsCard = ({ data = {}, cardRef, category, query }) => {
  const [isHovered, setIsHovered] = useState(false);
  const dispatch = useDispatch();

  // const educationValues = (data.educationLevels || []).sort((a, b) => {
  //   return a.code.localeCompare(b.code);
  // });

  const openSidebar = () => {
    logEvent("open_bookmark_popup", {
      type: "boclips",
      category: "boclips",
      entity: data.createdBy,
      title: data.title,
      published_date: data.releasedOn,
      query: query,
      url: `${searchAPI.boclipsLoadVideo}/${data.id}`,
    });

    dispatch(setSelectedBookmarkOption("notes"));
    dispatch(setLevel(""));
    setIsHovered(false);
    dispatch(setCollectionSourceId(data.collectionSourceId));
    dispatch(
      setSummaryInfoData({
        publisher: "Boclips",
        title: data.title,
        published_date: data.releasedOn,
        url: `${searchAPI.boclipsLoadVideo}/${data.id}`,
        utc_date_published: data.releasedOn,
      })
    );
    dispatch(setCollectionId(data.collectionId));
    dispatch(setIsCitationAvailable(data.isCitationAvailable));
    dispatch(setIsSummaryAvailable(data.isSummaryAvailable));
    dispatch(setReadingLevelAvailable(data.isReadingLevelAvailable));
    dispatch(setIsSidebarOpen(true));
  };

  const BookmarkOptions = () => {
    return (
      <Stack
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        sx={{
          cursor: "pointer",
          borderRadius: 1,
          "&:hover": {
            backgroundColor: "secondary.main",
          },
          pl: 1,
          pr: 1,
        }}
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
        gap={1}
        mt={1}
      >
        {data.collectionSourceId ? (
          <CollectionsLogo
            onClick={openSidebar}
            alt="Collections"
            style={{
              width: 18,
              height: 18,
              fill: "blue",
              color: "blue",
            }}
          />
        ) : (
          <Box
            sx={{
              width: 18,
              height: 18,
              cursor: "pointer",
            }}
          >
            {isHovered && (
              <CollectionsLogoHover
                onClick={openSidebar}
                alt="Collections"
                style={{
                  fill: "blue",
                  color: "blue",
                }}
              />
            )}
          </Box>
        )}
        <AccessibleIconButton
          sx={{
            p: 0,
            "&:hover": {
              backgroundColor: "transparent",
            },
          }}
          title="Open Bookmark"
          onClick={openSidebar}
        >
          <MoreHorizIcon />
        </AccessibleIconButton>
      </Stack>
    );
  };

  const imageUrl = data?.thumbnail?.href
    ? data?.thumbnail?.href
    : defaultBackground;

  const handleTitleClick = () => {
    logEvent("click_resultset_title", {
      type: "boclips",
      category: "boclips",
      entity: data.createdBy,
      title: data.title,
      published_date: data.releasedOn,
      url: `${searchAPI.boclipsLoadVideo}/${data.id}`,
      query: query,
    });
    dispatch(setBoclipsVideoId(data.id));
    dispatch(setBoclipsPopup(true));
  };

  return (
    <Box>
      <Box
        ref={cardRef}
        sx={{
          display: "grid",
          gridTemplateAreas: {
            xs: `
            "imageSection"
            "titleNotesSection"
            "divider"
          `,
            md: `
            "imageSection titleNotesSection"
            "imageSection titleNotesSection"
            "divider divider"
            `,
          },
          gridTemplateColumns: {
            xs: "1fr",
            md: "2fr 3fr",
          },
          gridTemplateRows: {
            xs: "auto auto auto",
            md: "auto 1fr auto",
          },
          gap: { xs: 0, md: 2 },
          width: "100%",
          boxShadow: "none",
          mt: 1,
        }}
      >
        <Box
          sx={{
            gridArea: "imageSection",
            display: "flex",
            pr: 1,
            gap: 1,
          }}
        >
          <Box
            onClick={handleTitleClick}
            sx={{
              position: "relative",
              width: "100%",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: { xs: "23vh", md: "100%" },
                backgroundColor: "rgba(0, 0, 0, 0.3)",
                borderRadius: 1,
              }}
            />
            <CardMedia
              component="img"
              sx={{
                width: "100%",
                height: { xs: "23vh", md: "100%" },
                borderRadius: 1,
                cursor: "pointer",
                objectFit: "cover",
                objectPosition: "center",
              }}
              image={imageUrl}
              alt="No Image Found"
            />
            <PlayCircleIcon
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                cursor: "pointer",
                fontSize: "3rem",
                color: "rgba(255, 255, 255, 0.8)",
                "&:hover": {
                  color: "rgba(255, 255, 255, 1)",
                },
              }}
            />
            <Typography
              sx={{
                position: "relative",
                bottom: "2.5rem",
                left: 8,
                backgroundColor: "rgba(0, 0, 0, 0.6)",
                padding: "2.5px 10px",
                fontSize: "1rem",
                borderRadius: "999px",
                color: "white",
                width: "fit-content",
              }}
            >
              {data?.playback?.duration &&
                formatDuration(data?.playback?.duration)}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            gridArea: "titleNotesSection",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography
            variant="body4"
            onClick={handleTitleClick}
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              lineHeight: "1.5em",
              flexShrink: 0,
              cursor: "pointer",
              "&:hover": {
                color: "rgb(18,89,151)",
              },
            }}
          >
            {data.title}
          </Typography>

          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              height: "100%",
              gap: { xs: 1.5, md: 1 },
            }}
          >
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
                maxHeight: { xs: "6vh", sm: "10vh" },
                lineHeight: { xs: "2vh", sm: "2vh" },
                mt: 0.5,
              }}
            >
              {dayjs(data.releasedOn).format("ll")}
              {` - `}
              {data.description}
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
              {data?.educationLevels?.map((education) => (
                <Chip sx={{ width: "fit-content" }} label={education.label} />
              ))}
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                variant="body2"
                color="textSecondary"
                noWrap={true}
                sx={{
                  color: "grey.600",
                }}
              >
                {data.createdBy}
              </Typography>
              <BookmarkOptions />
            </Box>
          </Box>
        </Box>
      </Box>
      <Divider sx={{ my: 1 }} />
    </Box>
  );
};
